import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface FestivalsContent {
  title: string;
  description: string;
  principalFestival: string;
  principalFestivalDetails: string;
  seasonalFestivals: string;
  seasonalDetails: string;
  celebrationTraditions: string;
  festivalSignificance: string;
}

const defaultContent: FestivalsContent = {
  title: 'Festivals',
  description: 'Experience the vibrant festivals celebrated at our temple',
  principalFestival: 'Annual Utsavam (Temple Festival)',
  principalFestivalDetails: 'The grand Annual Utsavam, spanning seven days in the Malayalam month of Meenam (March–April), is the crown jewel of the temple\'s festival calendar. Festivities commence with Kodiyettam (flag hoisting) and culminate in a spectacular Aarattu procession with caparisoned elephants, Panchavadyam, and Chenda Melam. Each day features special Kalasham poojas, cultural programs including Kathakali and Ottamthullal, and free Annadanam for all devotees. The festival draws over 10,000 worshippers from across Kerala.',
  seasonalFestivals: 'Navaratri, Shivaratri & More',
  seasonalDetails: 'Navaratri (nine nights in September–October) is celebrated with daily Saraswati Pooja, Vidyarambham for children, and classical music concerts. Maha Shivaratri sees 24-hour continuous Abhishekam with thousands of Bilva leaves. Vishu brings the Vishukkani darshan at dawn, followed by Sadya. Thiruvathira, Onam, and Mandala-Makaravilakku complete the annual cycle, ensuring the temple is alive with devotion every season.',
  celebrationTraditions: 'Temple festivals follow centuries-old Thanthra-Sastra protocols. Elephant processions (Ezhunnallippu), traditional percussion ensembles (Panchavadyam, Thayambaka), and fire-art performances (Theyyam during associated community festivals) are hallmarks. Devotees participate in Vilakku (lamp) offerings, creating a sea of illumination around the temple tank during evening processions.',
  festivalSignificance: 'Festivals serve as the spiritual heartbeat of the community, reuniting families, reinforcing cultural identity, and perpetuating oral and performing art traditions. The temple\'s festivals are recognized by the Kerala Tourism Department as heritage cultural events, drawing visitors and scholars from across the country.'
};

const festivalCalendar = [
  { month: 'Jan–Feb', name: 'Mandala Pooja & Makaravilakku', gradient: ['#3b82f6', '#2563eb'] },
  { month: 'Feb–Mar', name: 'Maha Shivaratri', gradient: ['#8b5cf6', '#7c3aed'] },
  { month: 'Mar–Apr', name: 'Annual Utsavam (7 Days)', gradient: ['#ef4444', '#dc2626'] },
  { month: 'Apr', name: 'Vishu Celebrations', gradient: ['#22c55e', '#16a34a'] },
  { month: 'Aug–Sep', name: 'Onam & Thiruvathira', gradient: ['#f59e0b', '#d97706'] },
  { month: 'Sep–Oct', name: 'Navaratri & Vidyarambham', gradient: ['#ec4899', '#db2777'] },
];

export default function Festivals() {
  const [content, setContent] = useState<FestivalsContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'about_content', 'festivals'), (docSnap) => {
      if (docSnap.exists()) {
        setContent(docSnap.data() as FestivalsContent);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading festivals:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Festivals — Kottayil Temple | ഉത്സവങ്ങൾ" description="Vibrant festivals and celebrations at Kottayil Temple (Kotta-Kovilakam Kshethram) — annual ulsavam, pooram, special ceremonies, and cultural events in Muchukunnu, Kerala." path="/about/festivals" keywords="Kottayil temple festivals, ulsavam, pooram, Kerala temple festivals, Muchukunnu festivals, ഉത്സവം, പൂരം, കൊട്ടയിൽ ക്ഷേത്രം ഉത്സവം, temple festivals Kozhikode" />
      <PageBanner title={content.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 40%, #f87171 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <CelebrationIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      {content.title}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      {content.description}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Over 15 major festivals are celebrated annually, blending ancient Vedic rituals with Kerala's vibrant cultural arts.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<CalendarMonthIcon sx={{ fontSize: '16px !important', color: '#ef4444 !important' }} />} label="15+ Annual Festivals" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#7f1d1d', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<CelebrationIcon sx={{ fontSize: '16px !important', color: '#fca5a5 !important' }} />} label="Sacred Traditions" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* FESTIVAL CALENDAR */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 3 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                    <CalendarMonthIcon sx={{ fontSize: 22, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>Festival Calendar</Typography>
                </Box>
              </Box>
              <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Grid container spacing={2}>
                  {festivalCalendar.map((f) => (
                    <Grid item xs={12} sm={6} key={f.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '16px', bgcolor: 'rgba(0,0,0,0.015)', border: '1px solid rgba(0,0,0,0.04)', transition: 'all 300ms ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.03)', transform: 'translateX(4px)' } }}>
                        <Box sx={{ px: 1.3, py: 0.6, borderRadius: '10px', background: `linear-gradient(135deg, ${f.gradient[0]}, ${f.gradient[1]})`, minWidth: 60, textAlign: 'center', boxShadow: `0 4px 12px ${f.gradient[0]}20` }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff' }}>{f.month}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>{f.name}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* CONTENT CARDS */}
            {[
              { title: content.principalFestival, text: content.principalFestivalDetails, icon: CelebrationIcon, gradient: ['#ef4444', '#dc2626'] },
              { title: content.seasonalFestivals, text: content.seasonalDetails, icon: MusicNoteIcon, gradient: ['#8b5cf6', '#7c3aed'] },
              { title: 'Celebration Traditions', text: content.celebrationTraditions, icon: TheaterComedyIcon, gradient: ['#22c55e', '#16a34a'] },
            ].map((card) => {
              const CIcon = card.icon;
              return (
                <Box key={card.title} sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 2.5 }}>
                  <Box sx={{ background: `linear-gradient(135deg, ${card.gradient[0]}, ${card.gradient[1]})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                        <CIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{card.title}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
                    <Typography sx={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 2, textAlign: 'justify' }}>{card.text}</Typography>
                  </Box>
                </Box>
              );
            })}

            {/* SIGNIFICANCE FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Festival Significance</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>The spiritual heartbeat of the community</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
                  {content.festivalSignificance}
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about/festivals" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
